import { Controller } from "@hotwired/stimulus"

// Drives the "Mandanten & Rollen" checklist on the new- and edit-member forms. Each
// tenant is a row with a checkbox, a role select and a type control (Mitarbeiter /
// Nur Zugriff). A row's role/type inputs are only active while its checkbox is
// ticked. One row is the seat/home tenant — it always becomes a full member, so its
// type control is hidden and a "Hauptmandant" badge is shown.
//
// On the NEW form the seat is the FIRST ticked row (dynamic). On the EDIT form the
// seat is fixed to the member's home tenant, passed via fixedPrimaryTenantId; that
// row's checkbox is disabled (rendered `locked`) so it can't be unticked.
//
// When an EXISTING membership's type is switched (rows carry data-current-type), an
// inline warning explains the consequence: switching to Nur Zugriff deactivates the
// tenant's employee, switching to Mitarbeiter creates a new one.
export default class extends Controller {
  static targets = [
    "row",
    "checkbox",
    "roleSelect",
    "typeControl",
    "primaryBadge",
    "warnToAccessOnly",
    "warnToEmployee",
  ]
  static values = { fixedPrimaryTenantId: String }

  connect() {
    this.rowTargets.forEach((row) => {
      this.syncRow(row)
      this.syncTypeWarning(row)
    })
    this.refreshPrimary()
  }

  toggle(event) {
    const row = event.target.closest("[data-group-member-assignments-target='row']")
    this.syncRow(row)
    this.syncTypeWarning(row)
    this.refreshPrimary()
  }

  typeChanged(event) {
    this.syncTypeWarning(event.target.closest("[data-group-member-assignments-target='row']"))
  }

  // Enable/disable a row's inputs to match its checkbox. A locked row (the fixed
  // home tenant) keeps its disabled checkbox but its role select stays usable.
  syncRow(row) {
    if (!row) return

    const checkbox = this.checkboxFor(row)
    const checked = checkbox?.checked ?? false
    const locked = checkbox?.disabled ?? false
    row.querySelectorAll("select, input[type='radio']").forEach((input) => {
      input.disabled = !checked
    })
    if (locked)
      row
        .querySelector("[data-group-member-assignments-target='roleSelect']")
        ?.removeAttribute("disabled")
  }

  // Badge on / type control hidden for the seat row; badge off / type control
  // visible for every other ticked row. The seat is the fixed home tenant in edit,
  // otherwise the first ticked row.
  refreshPrimary() {
    let firstCheckedSeen = false

    this.rowTargets.forEach((row) => {
      const checked = this.checkboxFor(row)?.checked ?? false
      const isPrimary =
        this.hasFixedPrimaryTenantIdValue && this.fixedPrimaryTenantIdValue
          ? row.dataset.tenantId === this.fixedPrimaryTenantIdValue
          : checked && !firstCheckedSeen
      if (checked && !firstCheckedSeen) firstCheckedSeen = true

      const badge = row.querySelector("[data-group-member-assignments-target='primaryBadge']")
      if (badge) badge.classList.toggle("hidden", !isPrimary)

      const typeControl = row.querySelector("[data-group-member-assignments-target='typeControl']")
      if (typeControl) typeControl.classList.toggle("hidden", isPrimary)
    })
  }

  // Show a warning only when an EXISTING membership's type differs from its original
  // value (rows without a data-current-type — i.e. the new form or a not-yet-member
  // tenant — never warn).
  syncTypeWarning(row) {
    if (!row) return

    const original = row.dataset.currentType
    const selected = row.querySelector("input[type='radio']:checked")?.value
    const checked = this.checkboxFor(row)?.checked ?? false
    const changed = checked && original && selected && selected !== original

    const toAccessOnly = row.querySelector(
      "[data-group-member-assignments-target='warnToAccessOnly']",
    )
    const toEmployee = row.querySelector("[data-group-member-assignments-target='warnToEmployee']")
    if (toAccessOnly)
      toAccessOnly.classList.toggle("hidden", !(changed && selected === "access_only"))
    if (toEmployee) toEmployee.classList.toggle("hidden", !(changed && selected === "employee"))
  }

  checkboxFor(row) {
    return row.querySelector("[data-group-member-assignments-target='checkbox']")
  }
}
